import { LandingBottomVideoCtaSection } from '@/components/landing/landing-bottom-video-cta-section'
import { LandingFaqSection } from '@/components/landing/landing-faq-section'
import { LandingFeatureBlockSection } from '@/components/landing/landing-feature-block-section'
import { LandingHeroVideo } from '@/components/landing/landing-hero-video'
import { LandingMediaSection } from '@/components/landing/landing-media-section'
import { LandingMissionCtaSection } from '@/components/landing/landing-mission-cta-section'
import { LandingPillarsSection } from '@/components/landing/landing-pillars-section'
import { LogoMarquee } from '@/components/landing/logo-marquee'
import { WhitepaperLeadModal } from '@/components/landing/whitepaper-lead-modal'
import { MarketingNav } from '@/components/layout/marketing-nav'
import { SiteFooter } from '@/components/layout/site-footer'
import { LandingJsonLd } from '@/components/seo/landing-json-ld'
import { PageMeta } from '@/components/seo/page-meta'
import { Button } from '@/components/ui/button'
import {
  LANDING_LEAD_MODAL_SESSION_KEYS,
  useLandingLeadModalTrigger,
} from '@/hooks/use-landing-lead-modal-trigger'
import { LANDING_FEATURE_BLOCKS } from '@/lib/landing-content'
import { PELGO_HOME_SEO } from '@/lib/pelgo-seo'
import { useEffect, useLayoutEffect, useState } from 'react'
import type React from 'react'
import { Link } from 'react-router-dom'

export function LandingPage() {
  const [isScrolled, setIsScrolled] = useState(false)
  const [leadModalOpen, setLeadModalOpen] = useState(false)

  useLayoutEffect(() => {
    if (!import.meta.env.DEV) return
    const params = new URLSearchParams(window.location.search)
    if (params.get('resetLeadModal') !== '1') return
    try {
      sessionStorage.removeItem(LANDING_LEAD_MODAL_SESSION_KEYS.dismissed)
      sessionStorage.removeItem(LANDING_LEAD_MODAL_SESSION_KEYS.submitted)
    } catch {
      // ignore
    }
    params.delete('resetLeadModal')
    const next = params.toString()
    const path = `${window.location.pathname}${next ? `?${next}` : ''}${window.location.hash}`
    window.history.replaceState(null, '', path)
  }, [])

  useLandingLeadModalTrigger(() => setLeadModalOpen(true))

  const scrollToSection = (e: React.MouseEvent<HTMLAnchorElement>, sectionId: string) => {
    e.preventDefault()
    const element = document.getElementById(sectionId)
    if (element) {
      const headerOffset = 80
      const elementPosition = element.getBoundingClientRect().top
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      })
    }
  }

  useEffect(() => {
    const videos = document.querySelectorAll('video')
    const cleanupFunctions: (() => void)[] = []

    for (const video of videos) {
      const videoElement = video as HTMLVideoElement
      videoElement.muted = true
      videoElement.volume = 0
      videoElement.loop = true
      videoElement.playsInline = true
      videoElement.setAttribute('playsinline', 'true')
      videoElement.setAttribute('webkit-playsinline', 'true')
      videoElement.setAttribute('x5-playsinline', 'true')
      videoElement.setAttribute('muted', 'true')
      videoElement.preload = 'auto'
      videoElement.defaultMuted = true
      videoElement.controls = false
      videoElement.removeAttribute('controls')
      videoElement.style.pointerEvents = 'none'
      videoElement.style.outline = 'none'

      const enforceMuted = () => {
        videoElement.muted = true
        videoElement.volume = 0
        videoElement.setAttribute('muted', 'true')
      }

      const mutedInterval = setInterval(enforceMuted, 100)
      videoElement.addEventListener('volumechange', enforceMuted)

      const attemptPlay = async () => {
        try {
          enforceMuted()
          videoElement.controls = false
          videoElement.removeAttribute('controls')
          await videoElement.play()
          enforceMuted()
          videoElement.style.pointerEvents = 'none'
          videoElement.style.outline = 'none'
          videoElement.style.border = 'none'
        } catch {
          // Play was prevented - will try again
        }
      }

      attemptPlay()

      const tryPlayWhenReady = () => {
        if (videoElement.readyState >= 2) {
          attemptPlay()
        }
      }

      videoElement.addEventListener('loadedmetadata', tryPlayWhenReady, { once: true })
      videoElement.addEventListener('loadeddata', tryPlayWhenReady, { once: true })
      videoElement.addEventListener('canplay', tryPlayWhenReady, { once: true })
      videoElement.addEventListener('canplaythrough', attemptPlay, { once: true })

      const interactionEvents = ['touchstart', 'touchend', 'click', 'scroll', 'pointerdown']
      const handleInteraction = () => {
        attemptPlay()
        for (const event of interactionEvents) {
          document.removeEventListener(event, handleInteraction, { capture: true })
        }
      }

      for (const event of interactionEvents) {
        document.addEventListener(event, handleInteraction, { once: true, capture: true })
      }

      const handleVisibilityChange = () => {
        if (!document.hidden) {
          enforceMuted()
          attemptPlay()
        }
      }
      document.addEventListener('visibilitychange', handleVisibilityChange)

      cleanupFunctions.push(() => {
        clearInterval(mutedInterval)
        videoElement.removeEventListener('volumechange', enforceMuted)
        document.removeEventListener('visibilitychange', handleVisibilityChange)
      })
    }

    return () => {
      for (const cleanup of cleanupFunctions) {
        cleanup()
      }
    }
  }, [])

  useEffect(() => {
    const handleScroll = () => {
      const heroHeight = 600
      setIsScrolled(window.scrollY > heroHeight)
    }

    window.addEventListener('scroll', handleScroll)
    handleScroll()

    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    const handleHashNavigation = () => {
      const hash = window.location.hash
      if (hash) {
        const sectionId = hash.substring(1)
        const element = document.getElementById(sectionId)
        if (element) {
          setTimeout(() => {
            const headerOffset = 80
            const elementPosition = element.getBoundingClientRect().top
            const offsetPosition = elementPosition + window.pageYOffset - headerOffset

            window.scrollTo({
              top: offsetPosition,
              behavior: 'smooth',
            })
          }, 100)
        }
      }
    }

    handleHashNavigation()
  }, [])

  return (
    <div className="flex flex-col min-h-screen bg-white">
      <PageMeta config={PELGO_HOME_SEO} />
      <LandingJsonLd />
      <MarketingNav variant="landing" isScrolled={isScrolled} onScrollToSection={scrollToSection} />
      <WhitepaperLeadModal open={leadModalOpen} onOpenChange={setLeadModalOpen} />

      <section className="pt-24 md:pt-32 relative overflow-hidden min-h-[560px] md:min-h-[720px] flex items-center">
        <LandingHeroVideo />
        <div className="container mx-auto px-4 md:px-16 py-12 md:py-32 relative z-20">
          <div className="max-w-4xl scroll-fade-up in-view">
            <h1 className="text-4xl sm:text-5xl md:text-[80px] font-display text-white leading-none mb-4 md:mb-6 tracking-[-1.6px]">
              Offboarding sucks.
              <br />
              <span className="font-display text-brand-light">But it doesn't have to.</span>
            </h1>
            <p className="text-base sm:text-lg md:text-2xl font-medium text-white/90 mb-8 md:mb-12 leading-relaxed max-w-3xl tracking-[-0.48px]">
              Meet the world's most comprehensive, end-to-end offboarding platform.
            </p>
            <p className="sr-only">
              Pelgo is a modern outplacement and severance platform for enterprises, combining
              intelligent severance administration, next-gen outplacement, and severance
              maximization to support workforce mobility.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <Button
                asChild
                className="bg-pelgo-blue-light hover:bg-pelgo-blue-light/90 text-white rounded-full px-6 py-3 text-sm font-semibold min-h-[48px]"
              >
                <Link to="/contact">Request Free Trial</Link>
              </Button>
              <Button
                asChild
                variant="outline"
                className="border-white text-white rounded-full px-6 py-3 text-sm font-semibold bg-transparent hover:bg-white/10 hover:text-white min-h-[48px]"
              >
                <Link to="/resources/whitepapers">Download Whitepaper</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <LogoMarquee />

      <LandingMediaSection />

      <LandingPillarsSection onScrollToSection={scrollToSection} />

      {LANDING_FEATURE_BLOCKS.map((block) => (
        <LandingFeatureBlockSection key={block.id} block={block} />
      ))}

      <LandingFaqSection />
      <LandingMissionCtaSection />
      <LandingBottomVideoCtaSection />

      <SiteFooter className="bg-white w-full scroll-fade-up p-4 md:p-8" />
    </div>
  )
}
